import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { INVOICE_DEFAULTS, INVOICE_BLOCKS } from "@/lib/printTemplates";
import { InvoiceDoc, SAMPLE_INVOICE } from "@/components/print/InvoiceDoc";
import { printInvoice, downloadInvoicePdf } from "@/lib/printing";
import { setPath } from "@/lib/useHistory";
import { ImageField } from "@/components/ImageField";
import { SortableList } from "@/components/SortableList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Printer, FileDown, Lock, Eye } from "lucide-react";
import { useConfigEditor, EditorBar, Field, Panel } from "./common";

const FONTS = ["Manrope", "Arial", "Times New Roman", "Georgia", "Cormorant Garamond"];

function NextNumber() {
  const [next, setNext] = useState(null);
  const [val, setVal] = useState("");
  useEffect(() => { api.get("/invoices/meta/next-number").then(({ data }) => { setNext(data.next); setVal(data.next); }).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.post("/invoices/meta/next-number", { next: Number(val) }); setNext(data.next); toast.success("Номерацията е обновена"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Номерация на фактурите">
      <p className="text-xs text-muted-foreground">Следващ номер: <b data-testid="invoice-next-number">{String(next || 1).padStart(10, "0")}</b>. Номерът се дава автоматично и не може да се повтори. Може само да се увеличи (напр. ако продължавате номерация от друга програма).</p>
      <div className="flex gap-2"><Input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="invoice-next-input" /><Button variant="outline" onClick={save} data-testid="invoice-next-save">Задай</Button></div>
    </Panel>
  );
}

export default function InvoiceTab() {
  const ed = useConfigEditor("print_invoice", INVOICE_DEFAULTS);
  const [company, setCompany] = useState({});
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/config/company").then(({ data }) => setCompany(data)).catch(() => {}); }, []);
  const t = ed.value;
  if (!t) return null;
  const set = (patch) => ed.set((s) => ({ ...s, ...patch }));
  const inv = SAMPLE_INVOICE(company);
  const editText = (path, value) => {
    if (path.startsWith("columns.")) {
      const key = path.split(".")[1];
      return ed.set((s) => ({ ...s, columns: s.columns.map((c) => (c.key === key ? { ...c, label: value } : c)) }));
    }
    ed.set((s) => setPath(s, path, value));
  };
  const toggleBlock = (b) => set({ hidden: t.hidden.includes(b) ? t.hidden.filter((x) => x !== b) : [...t.hidden, b] });

  return (
    <div data-testid="invoice-tab">
      <EditorBar ed={ed} testid="invoice">
        <Button variant="outline" onClick={() => printInvoice(t, inv)} className="gap-1" data-testid="invoice-test-print"><Printer className="w-4 h-4" /> Тестов печат A4</Button>
        <Button variant="outline" onClick={() => downloadInvoicePdf(t, inv).catch((e) => toast.error(e.message))} className="gap-1" data-testid="invoice-test-pdf"><FileDown className="w-4 h-4" /> PDF</Button>
      </EditorBar>
      <div className="grid 2xl:grid-cols-[380px_1fr] xl:grid-cols-[340px_1fr] gap-5">
        <div className="space-y-4">
          <Panel title="Принтер и страница (A4, портрет)">
            <Field label="Име на принтера (за справка)" value={t.printer_name} onChange={(v) => set({ printer_name: v })} placeholder="напр. HP LaserJet" testid="invoice-printer-name" />
            <div className="grid grid-cols-4 gap-2">
              {["top", "right", "bottom", "left"].map((k) => (
                <Field key={k} label={{ top: "Горе", right: "Дясно", bottom: "Долу", left: "Ляво" }[k]} type="number" value={t.margins_mm[k]}
                  onChange={(v) => set({ margins_mm: { ...t.margins_mm, [k]: v } })} testid={`invoice-margin-${k}`} />
              ))}
            </div>
          </Panel>
          <Panel title="Стил">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Шрифт (pt)" type="number" value={t.font_size} onChange={(v) => set({ font_size: v })} testid="invoice-font-size" />
              <Field label="Разстояние (мм)" type="number" value={t.spacing_mm} onChange={(v) => set({ spacing_mm: v })} testid="invoice-spacing" />
              <div><Label className="text-xs">Акцентен цвят</Label><Input type="color" value={t.accent} onChange={(e) => set({ accent: e.target.value })} data-testid="invoice-accent" /></div>
              <div><Label className="text-xs">Цвят на текста</Label><Input type="color" value={t.text_color} onChange={(e) => set({ text_color: e.target.value })} data-testid="invoice-text-color" /></div>
            </div>
            <div><Label className="text-xs">Шрифт</Label>
              <select value={t.font_family} onChange={(e) => set({ font_family: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="invoice-font-family">
                {FONTS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
          </Panel>
          <Panel title="Лого">
            <ImageField label="Лого на фактурата" value={t.logo_url} onChange={(v) => set({ logo_url: v })} testid="invoice-logo" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Ширина (мм)" type="number" value={t.logo_mm} onChange={(v) => set({ logo_mm: v })} testid="invoice-logo-size" />
              <div><Label className="text-xs">Позиция</Label>
                <select value={t.logo_align} onChange={(e) => set({ logo_align: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="invoice-logo-align">
                  <option value="left">Вляво</option><option value="right">Вдясно</option>
                </select>
              </div>
            </div>
          </Panel>
          <Panel title="Елементи (влачете за подредба)">
            <SortableList ids={t.blocks} onReorder={(blocks) => set({ blocks })}>
              {(id, handle) => (
                <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 border ${sel === id ? "border-gold bg-gold/10" : "border-gold/15"}`}>
                  {handle}
                  <button className="flex-1 text-left text-sm" onClick={() => setSel(id)}>{INVOICE_BLOCKS[id].label}</button>
                  {INVOICE_BLOCKS[id].locked ? <Lock className="w-4 h-4 text-forest/40" title="Задължителен елемент" />
                    : <Switch checked={!t.hidden.includes(id)} onCheckedChange={() => toggleBlock(id)} data-testid={`invoice-block-${id}`} />}
                </div>
              )}
            </SortableList>
          </Panel>
          <Panel title="Колони на таблицата">
            <SortableList ids={t.columns.map((c) => c.key)} onReorder={(keys) => set({ columns: keys.map((k) => t.columns.find((c) => c.key === k)) })}>
              {(key, handle) => {
                const c = t.columns.find((x) => x.key === key);
                return (
                  <div className="flex items-center gap-2">
                    {handle}
                    <Input value={c.label} onChange={(e) => editText(`columns.${key}`, e.target.value)} className="h-8" data-testid={`invoice-col-label-${key}`} />
                    {c.locked ? <Lock className="w-4 h-4 text-forest/40 shrink-0" /> : (
                      <Switch checked={c.visible} onCheckedChange={(v) => set({ columns: t.columns.map((x) => (x.key === key ? { ...x, visible: v } : x)) })} data-testid={`invoice-col-${key}`} />
                    )}
                  </div>
                );
              }}
            </SortableList>
          </Panel>
          <NextNumber />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Преглед – кликнете върху текст с пунктир, за да го промените директно</div>
          <div className="bg-neutral-200 rounded-2xl p-4 overflow-auto max-h-[85vh]">
            <div className="shadow-xl mx-auto w-fit" data-testid="invoice-preview">
              <InvoiceDoc tpl={t} inv={inv} edit={editText} selected={sel} onSelect={setSel} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
